import { useMemo } from 'react';

import { useGenericSearch } from '@/hooks/useGenericSearch';
import { MonitoringCounts, Statistic } from '@/types';
import { QueryStatus, useMergeStatus } from '@/utils/api';

/**
 * Checks if a statistic value represents a cloud resource type
 * (e.g., "AWS::EC2::Instance", "microsoft.compute/virtualmachines")
 */
function isCloudResourceType(value: string): boolean {
  const lower = value.toLowerCase();
  return (
    value.startsWith('AWS::') ||
    lower.startsWith('microsoft.') ||
    value.startsWith('GCP::') ||
    lower.startsWith('google.') ||
    value.startsWith('DigitalOcean::')
  );
}

/**
 * Maps cloud resource type prefix to its provider name
 */
function mapResourceToProvider(value: string): string {
  const lower = value.toLowerCase();
  if (value.startsWith('AWS::')) return 'amazon';
  if (lower.startsWith('microsoft.')) return 'azure';
  if (value.startsWith('GCP::') || lower.startsWith('google.')) return 'gcp';
  if (value.startsWith('DigitalOcean::')) return 'digitalocean';
  return value;
}

/**
 * Accumulates stats for non-cloud-resource-type values.
 * Maps known aliases (aws, s3) to canonical provider names.
 */
function getStatsObject(stats: Statistic[]) {
  return stats
    .filter(stat => !isCloudResourceType(stat.value))
    .reduce(
      (acc, curr) => {
        let key = curr.value;
        // Map known AWS aliases to canonical provider name
        if (key === 'aws' || key === 's3') key = 'amazon';
        const count = (curr.data?.count as number) || 0;

        return {
          ...acc,
          [key]: ((acc[key] as number) || 0) + count,
        };
      },
      {} as Record<string, number>
    );
}

/**
 * Accumulates stats for cloud resource types (AWS::*, microsoft.*, etc.),
 * grouped by provider name.
 */
function getCloudResourceStats(stats: Statistic[]) {
  return stats
    .filter(stat => isCloudResourceType(stat.value))
    .reduce(
      (acc, curr) => {
        const key = mapResourceToProvider(curr.value);
        const count = (curr.data?.count as number) || 0;

        return {
          ...acc,
          [key]: ((acc[key] as number) || 0) + count,
        };
      },
      {} as Record<string, number>
    );
}

export function useGetMonitoringCounts(options?: { email?: string }): {
  data: MonitoringCounts;
  status: QueryStatus;
  error: Error | null;
  refetch: () => void;
} {
  const cacheOptions = {
    staleTime: 30000, // 30 seconds - data stays fresh
    gcTime: 300000, // 5 minutes - keep in cache
    refetchOnMount: false, // Only refetch if stale
    refetchOnWindowFocus: false, // Prevent focus refetch
    email: options?.email,
  };

  const {
    data: externalAssetCountsGenericSearch,
    status: externalAssetCountsGenericSearchStatus,
    error: externalError,
    refetch: refetchExternal,
  } = useGenericSearch(
    {
      query: '#statistic#monitoring_count#externalasset#now#',
    },
    cacheOptions
  );
  const {
    data: internalAssetCountsGenericSearch,
    status: internalAssetCountsGenericSearchStatus,
    error: internalError,
    refetch: refetchInternal,
  } = useGenericSearch(
    {
      query: '#statistic#monitoring_count#internalasset#now#',
    },
    cacheOptions
  );
  const {
    data: assetCountsByType,
    status: assetCountsByTypeStatus,
    error: assetTypeError,
    refetch: refetchAssetType,
  } = useGenericSearch(
    {
      query: '#statistic#type#type#now#',
    },
    cacheOptions
  );
  const {
    data: webapps,
    status: webappsStatus,
    error: webappsError,
    refetch: refetchWebapps,
  } = useGenericSearch(
    {
      query: '#statistic#webapps#webapps#now#',
    },
    cacheOptions
  );
  const {
    data: codeRepos,
    status: codeReposStatus,
    error: codeReposError,
    refetch: refetchCodeRepos,
  } = useGenericSearch(
    {
      query: '#statistic#code_repos#',
    },
    cacheOptions
  );

  const externalAssetCountsGenericSearchStats =
    externalAssetCountsGenericSearch?.statistics;
  const internalAssetCountsGenericSearchStats =
    internalAssetCountsGenericSearch?.statistics;
  const assetCountsByTypeStats = assetCountsByType?.statistics;
  const webappsStats = webapps?.statistics;
  const codeReposStats = codeRepos?.statistics;

  const counts = useMemo(() => {
    // Regular external stats (excludes cloud resource types like AWS::*, microsoft.*)
    const {
      amazon: externalAmazon = 0,
      azure: externalAzure = 0,
      gcp: externalGcp = 0,
      digitalocean: externalDigitalocean = 0,
      ipv4: externalIpv4 = 0,
      ipv6: externalIpv6 = 0,
      tld: externalDomain = 0,
      domain: externalSubdomain = 0,
    } = getStatsObject(externalAssetCountsGenericSearchStats || []);

    // Cloud resource types from external stats (AWS::EC2::Instance, microsoft.compute/*, etc.)
    const {
      amazon: externalAmazonResources = 0,
      azure: externalAzureResources = 0,
      gcp: externalGcpResources = 0,
      digitalocean: externalDigitaloceanResources = 0,
    } = getCloudResourceStats(externalAssetCountsGenericSearchStats || []);

    const {
      amazon: internalAmazon = 0,
      azure: internalAzure = 0,
      gcp: internalGcp = 0,
      digitalocean: internalDigitalocean = 0,
      ipv4: internalIpv4 = 0,
    } = getStatsObject(internalAssetCountsGenericSearchStats || []);

    // Cloud resource types from internal stats
    const {
      amazon: internalAmazonResources = 0,
      azure: internalAzureResources = 0,
      gcp: internalGcpResources = 0,
      digitalocean: internalDigitaloceanResources = 0,
    } = getCloudResourceStats(internalAssetCountsGenericSearchStats || []);

    const {
      ADDomain = 0,
      ADUser = 0,
      ADComputer = 0,
    } = getStatsObject(assetCountsByTypeStats || []);

    const {
      webapplication = 0,
      webseed = 0,
      webllm = 0,
    } = getStatsObject(webappsStats || []);

    // Build code repo counts from dedicated code_repos query
    const codeRepoCounts = (codeReposStats || []).reduce(
      (acc, stat) => {
        const provider = stat.value;
        const count = (stat.data?.count as number) || 0;
        if (provider && provider !== 'unknown') {
          acc[provider] = (acc[provider] || 0) + count;
        }
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      cloud: {
        amazon: {
          external: externalAmazon,
          internal: internalAmazon,
          resources: externalAmazonResources + internalAmazonResources,
        },
        azure: {
          external: externalAzure,
          internal: internalAzure,
          resources: externalAzureResources + internalAzureResources,
        },
        gcp: {
          external: externalGcp,
          internal: internalGcp,
          resources: externalGcpResources + internalGcpResources,
        },
        digitalocean: {
          external: externalDigitalocean,
          internal: internalDigitalocean,
          resources:
            externalDigitaloceanResources + internalDigitaloceanResources,
        },
      },
      externalNetwork: {
        ipv4: externalIpv4,
        ipv6: externalIpv6,
        domain: externalDomain,
        subdomain: externalSubdomain,
      },
      applications: {
        webapplication,
        webseed,
        webllm,
      },
      internalNetwork: {
        ipv4: internalIpv4,
        ADDomain,
        ADUser,
        ADComputer,
      },
      code: {
        github: codeRepoCounts['github'] || 0,
        gitlab: codeRepoCounts['gitlab'] || 0,
        bitbucket: codeRepoCounts['bitbucket'] || 0,
        azuredevops: codeRepoCounts['azuredevops'] || 0,
      },
    } as MonitoringCounts;
  }, [
    externalAssetCountsGenericSearchStats?.length ?? 0,
    internalAssetCountsGenericSearchStats?.length ?? 0,
    assetCountsByTypeStats?.length ?? 0,
    webappsStats?.length ?? 0,
    codeReposStats?.length ?? 0,
  ]);

  // Aggregate errors
  const error =
    externalError ||
    internalError ||
    assetTypeError ||
    webappsError ||
    codeReposError ||
    null;

  // Combined refetch function
  const refetch = () => {
    refetchExternal();
    refetchInternal();
    refetchAssetType();
    refetchWebapps();
    refetchCodeRepos();
  };

  return {
    data: counts,
    status: useMergeStatus(
      externalAssetCountsGenericSearchStatus,
      internalAssetCountsGenericSearchStatus,
      assetCountsByTypeStatus,
      webappsStatus,
      codeReposStatus
    ),
    error,
    refetch,
  };
}
